import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { TrendingUp, Users, Briefcase, Sprout, Download, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Agriculture() {
  const { t, language } = useLanguage();

  const assets = {
    frenchMethod: 'https://static.prod-images.emergentagent.com/jobs/7cf829f0-16ef-4383-a637-469dccd1e538/images/fe2b253ee8bfe9821d916bac0fe3601db668ca6070ed58881539ce577e6a95cf.png',
    agricultureHero: 'https://images.pexels.com/photos/10269715/pexels-photo-10269715.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  };

  const pdfUrls = {
    frenchMethod: 'https://customer-assets.emergentagent.com/job_e45f36f2-8a83-4b94-a9f1-cfa3989b34bb/artifacts/e0rvt04q_French%20Method%20%E2%80%93%20Un%20programme%20structurant%20pour%20l%27emploi%20des%20jeunes%2C%20la%20souverainet%C3%A9%20alimentaire%20et%20l%27entrepreneuriat%20agricole.pdf',
    greenSolutions: 'https://customer-assets.emergentagent.com/job_e45f36f2-8a83-4b94-a9f1-cfa3989b34bb/artifacts/51fmmndf_GREEN%20SOLUTIONS%20Solutions%20innovantes%20pour%20l%27agriculture.pdf'
  };

  // Contenu multilingue pour les PDF
  const pdfContent = {
    fr: {
      frenchMethodTitle: 'Télécharger le Document FRENCH METHOD',
      frenchMethodDesc: 'Un programme structurant pour l\'emploi des jeunes, la souveraineté alimentaire et l\'entrepreneuriat agricole. Découvrez comment transformer la jeunesse africaine en acteurs de richesse durable.',
      greenSolutionsTitle: 'Télécharger GREEN SOLUTIONS',
      greenSolutionsDesc: 'Solutions innovantes pour l\'agriculture durable. Technologies vertes et méthodes révolutionnaires pour une Afrique autosuffisante.',
      downloadBtn: 'Télécharger le PDF',
      documentsSection: 'Documents Stratégiques'
    },
    en: {
      frenchMethodTitle: 'Download FRENCH METHOD Document',
      frenchMethodDesc: 'A structuring program for youth employment, food sovereignty and agricultural entrepreneurship. Discover how to transform African youth into sustainable wealth creators.',
      greenSolutionsTitle: 'Download GREEN SOLUTIONS',
      greenSolutionsDesc: 'Innovative solutions for sustainable agriculture. Green technologies and revolutionary methods for a self-sufficient Africa.',
      downloadBtn: 'Download PDF',
      documentsSection: 'Strategic Documents'
    },
    pt: {
      frenchMethodTitle: 'Baixar o Documento FRENCH METHOD',
      frenchMethodDesc: 'Um programa estruturante para o emprego jovem, soberania alimentar e empreendedorismo agrícola. Descubra como transformar a juventude africana em criadores de riqueza sustentável.',
      greenSolutionsTitle: 'Baixar GREEN SOLUTIONS',
      greenSolutionsDesc: 'Soluções inovadoras para agricultura sustentável. Tecnologias verdes e métodos revolucionários para uma África autossuficiente.',
      downloadBtn: 'Baixar PDF',
      documentsSection: 'Documentos Estratégicos'
    },
    it: {
      frenchMethodTitle: 'Scarica il Documento FRENCH METHOD',
      frenchMethodDesc: 'Un programma strutturante per l\'occupazione giovanile, la sovranità alimentare e l\'imprenditoria agricola. Scopri come trasformare i giovani africani in creatori di ricchezza sostenibile.',
      greenSolutionsTitle: 'Scarica GREEN SOLUTIONS',
      greenSolutionsDesc: 'Soluzioni innovative per l\'agricoltura sostenibile. Tecnologie verdi e metodi rivoluzionari per un\'Africa autosufficiente.',
      downloadBtn: 'Scarica PDF',
      documentsSection: 'Documenti Strategici'
    },
    ru: {
      frenchMethodTitle: 'Скачать документ FRENCH METHOD',
      frenchMethodDesc: 'Структурирующая программа для занятости молодёжи, продовольственного суверенитета и сельскохозяйственного предпринимательства. Узнайте, как превратить африканскую молодёжь в создателей устойчивого богатства.',
      greenSolutionsTitle: 'Скачать GREEN SOLUTIONS',
      greenSolutionsDesc: 'Инновационные решения для устойчивого сельского хозяйства. Зелёные технологии и революционные методы для самодостаточной Африки.',
      downloadBtn: 'Скачать PDF',
      documentsSection: 'Стратегические документы'
    },
    zh: {
      frenchMethodTitle: '下载 FRENCH METHOD 文件',
      frenchMethodDesc: '青年就业、粮食主权和农业创业的结构化计划。了解如何将非洲青年转变为可持续财富创造者。',
      greenSolutionsTitle: '下载 GREEN SOLUTIONS',
      greenSolutionsDesc: '可持续农业创新解决方案。绿色技术和革命性方法，打造自给自足的非洲。',
      downloadBtn: '下载 PDF',
      documentsSection: '战略文件'
    }
  };

  const pc = pdfContent[language] || pdfContent.fr;

  const trackDownload = async (documentName) => {
    try {
      await fetch(`${API_URL}/api/track/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ document_name: documentName, language })
      });
    } catch (err) {
      console.error('Error tracking download:', err);
    }
  };

  const handleDownload = (url, documentName) => {
    trackDownload(documentName);
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-white pt-32">
      <section className="py-24 md:py-32 bg-gradient-to-br from-emerald-50 to-white" data-testid="agriculture-hero">
        <div className="max-w-6xl mx-auto px-6">
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
            {t('agriculture.title')}
          </h1>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-3xl">
            {t('agriculture.subtitle')}
          </p>
        </div>
      </section>

      <section className="py-16" data-testid="french-method-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-emerald-100 rounded-full mb-6">
                <p className="text-sm font-semibold text-emerald-600 uppercase tracking-wider">
                  {t('agriculture.frenchMethod.subtitle')}
                </p>
              </div>
              <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-6">
                {t('agriculture.frenchMethod.title')}
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                {t('agriculture.frenchMethod.description')}
              </p>

              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                {t('agriculture.frenchMethod.impact')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: TrendingUp, key: 'impact1' },
                  { icon: Sprout, key: 'impact2' },
                  { icon: Users, key: 'impact3' },
                  { icon: Briefcase, key: 'impact4' },
                ].map((item) => (
                  <div key={item.key} className="flex items-start gap-3 bg-emerald-50 rounded-xl p-4">
                    <item.icon className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-1" />
                    <span className="text-sm text-slate-700 font-medium">
                      {t(`agriculture.frenchMethod.${item.key}`)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg">
              <img src={assets.frenchMethod} alt="French Method Training" className="w-full" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50" data-testid="green-solutions-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-6">
              {t('agriculture.greenSolutions.title')}
            </h2>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto">
              {t('agriculture.greenSolutions.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white border border-slate-200 rounded-2xl p-8">
              <div className="w-16 h-16 bg-sky-100 rounded-2xl flex items-center justify-center mb-6">
                <Sprout className="w-8 h-8 text-sky-600" />
              </div>
              <h3 className="text-2xl font-semibold text-slate-900 mb-3">
                {t('agriculture.greenSolutions.ecosorb')}
              </h3>
              <p className="text-base text-slate-600">
                {t('agriculture.greenSolutions.ecosorbDesc')}
              </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mb-6">
                <TrendingUp className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-2xl font-semibold text-slate-900 mb-3">
                {t('agriculture.greenSolutions.ecofert')}
              </h3>
              <p className="text-base text-slate-600">
                {t('agriculture.greenSolutions.ecofertDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-3xl p-12 text-white text-center">
            <h3 className="text-3xl md:text-4xl font-semibold mb-4">
              {language === 'fr' && 'Rendement x10 | Autonomie Alimentaire | Entrepreneuriat'}
              {language === 'en' && 'x10 Yield | Food Autonomy | Entrepreneurship'}
              {language === 'pt' && 'Rendimento x10 | Autonomia Alimentar | Empreendedorismo'}
              {language === 'it' && 'Rendimento x10 | Autonomia Alimentare | Imprenditorialità'}
              {language === 'ru' && 'Урожайность x10 | Продовольственная автономия | Предпринимательство'}
              {language === 'zh' && '产量x10 | 粮食自主 | 创业精神'}
            </h3>
            <p className="text-base md:text-lg opacity-90 max-w-2xl mx-auto">
              {language === 'fr' && 'Le French Method transforme la jeunesse africaine en producteurs de richesse durable.'}
              {language === 'en' && 'The French Method transforms African youth into sustainable wealth producers.'}
              {language === 'pt' && 'O Método Francês transforma a juventude africana em produtores de riqueza sustentável.'}
              {language === 'it' && 'Il Metodo Francese trasforma i giovani africani in produttori di ricchezza sostenibile.'}
              {language === 'ru' && 'French Method превращает африканскую молодёжь в создателей устойчивого богатства.'}
              {language === 'zh' && 'French Method 将非洲青年转变为可持续财富创造者。'}
            </p>
          </div>
        </div>
      </section>

      {/* Documents Stratégiques - PDF Downloads */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white" data-testid="strategic-documents-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-emerald-100 rounded-full mb-4">
              <p className="text-sm font-semibold text-emerald-600 uppercase tracking-wider">
                <FileText className="w-4 h-4 inline mr-2" />
                {pc.documentsSection}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* French Method PDF */}
            <div className="bg-white rounded-3xl border border-emerald-200 p-8 shadow-lg hover:shadow-xl transition-all group">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <FileText className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" style={{fontFamily: 'Playfair Display, serif'}}>
                FRENCH METHOD
              </h3>
              <p className="text-base text-slate-600 leading-relaxed mb-6">
                {pc.frenchMethodDesc}
              </p>
              <button
                onClick={() => handleDownload(pdfUrls.frenchMethod, 'FRENCH_METHOD')}
                data-testid="download-french-method-btn"
                className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold transition-colors"
              >
                <Download className="w-5 h-5" />
                {pc.downloadBtn}
              </button>
            </div>

            {/* Green Solutions PDF */}
            <div className="bg-white rounded-3xl border border-sky-200 p-8 shadow-lg hover:shadow-xl transition-all group">
              <div className="w-16 h-16 bg-sky-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Sprout className="w-8 h-8 text-sky-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" style={{fontFamily: 'Playfair Display, serif'}}>
                GREEN SOLUTIONS
              </h3>
              <p className="text-base text-slate-600 leading-relaxed mb-6">
                {pc.greenSolutionsDesc}
              </p>
              <button
                onClick={() => handleDownload(pdfUrls.greenSolutions, 'GREEN_SOLUTIONS')}
                data-testid="download-green-solutions-btn"
                className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-sky-600 hover:bg-sky-700 text-white rounded-xl font-semibold transition-colors"
              >
                <Download className="w-5 h-5" />
                {pc.downloadBtn}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
